import React, {useEffect, useState} from "react";
import {DoopUser} from "../Interfaces/IDOOP";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import {sendUserData} from "../BackendFacade/apiCalls";
import {HubPathRouting} from "../../../HubFramework/pathBuilder";
import OdxModal from "../../../OdxComponents/NativeOdx/Modal/Odx-Modal";
import OdxInputField from "../../../OdxComponents/NativeOdx/FormControls/Odx-InputField";
import OdxSelect, {OdxSelectOptionProps} from "../../../OdxComponents/NativeOdx/FormControls/Odx-Select";
import OdxCheckbox from "../../../OdxComponents/NativeOdx/FormControls/Odx-Checkbox";
import {isUserroleSufficient} from "../../../Utils/authorization";
import {UserRoles} from "../../../Utils";
import {useDoopData} from "../doopDataContext";

interface EditUserModalProps {
    user?: DoopUser | null;
    closePopup: () => void;
    deleteUser: (email: string) => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({user, closePopup, deleteUser}) => {
    const [Name, setName] = useState(user ? user.name : "");
    const [Email, setEmail] = useState(user ? user.email : "");
    const [CanAcceptOrders, setCanAcceptOrders] = useState(user ? user.canAcceptOrders : false);
    const [CanInitiateProcess, setCanInitiateProcess] = useState(user ? user.canInitiateProcess : false);
    const [IsContractor, setIsContractor] = useState(user ? user.isContractor : false);
    const [IsOrderInformed, setIsOrderInformed] = useState(user ? user.isOrderInformed : false);
    const [areas, setAreas] = useState<string[]>(user?.areas ?? []);

    const {areas: availableAreas, refresh} = useDoopData();
    const {setLoading, createErrorToast} = useMain();

    useEffect(() => {
        if (!user) {
            setAreas([]);
            return;
        }
        setAreas(user.areas);
    }, [user]);

    const submitUser = () => {
        if (Name === "" || Email === "") {
            createErrorToast("Error", "Name and Email are required");
            return;
        }
        let data: DoopUser = {
            name: Name,
            email: Email,
            canAcceptOrders: CanAcceptOrders,
            canInitiateProcess: CanInitiateProcess,
            isContractor: IsContractor,
            isOrderInformed: IsOrderInformed,
            areas: areas,
        };
        setLoading(true);
        sendUserData(data, HubPathRouting.currentProject.id, HubPathRouting.currentModule.id).then(
            data2 => {
                if (!data2.success) {
                    createErrorToast("Error", data2.message);
                } else {
                    refresh().then(() => {
                        closePopup();
                    }).catch(() => {
                        createErrorToast("Error", "Failed to refresh project data");
                    });
                }
                setLoading(false);
            }
        );
    };

    const getCurrentDoopAreas = () => {
        let optionList: OdxSelectOptionProps[] = [];
        if (availableAreas) {
            optionList = availableAreas.map((area) => {
                const split = area.name.split('\\');
                const name = split[split.length - 1];
                return {label: name, value: area.name};
            });
        }
        return optionList;
    };

    const canEdit = isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id);

    return (
        <OdxModal
            size="small"
            title="User Configuration"
            onClose={closePopup}
            isOpen={true}
            variant="sidesheet"
            footerButtons={
                canEdit
                    ? [
                        <odx-button
                            key="delete"
                            variant="danger"
                            size="lg"
                            type="button"
                            onClick={() => deleteUser(user?.email || "")}
                        >
                            Delete
                        </odx-button>,
                        <odx-button
                            key="save"
                            id="Doop_User_Edit_Apply"
                            variant="accent"
                            size="lg"
                            type="button"
                            onClick={submitUser}
                        >
                            Save
                        </odx-button>
                    ]
                    : []
            }
        >
            <div className="space-y-4">
                <OdxInputField
                    placeholder="Name"
                    isRequired={true}
                    id="userName"
                    label="Name"
                    value={Name}
                    onChange={(e) => setName(e.target.value)}
                />
                <OdxInputField
                    placeholder="Email"
                    isRequired={true}
                    id="userEmail"
                    label="Email"
                    value={Email}
                    onChange={(e) => setEmail(e.target.value)}
                />
                <OdxSelect
                    options={getCurrentDoopAreas()}
                    value={areas}
                    id={"Doop_User_Edit_Areas"}
                    onChange={(selectedValues) => {
                        setAreas(Array.isArray(selectedValues) ? selectedValues : []);
                    }}
                    placeholder="Area"
                    isMulti={true}
                />
                <div className="flex flex-col space-y-2">
                    <OdxCheckbox
                        label="Is PO/PM"
                        id={"Doop_User_Edit_PO"}
                        checked={CanInitiateProcess}
                        onChange={(e) => setCanInitiateProcess(e.target.checked)}
                    />
                    <OdxCheckbox
                        label="Is Service Manager"
                        id={"Doop_User_Edit_ServiceMng"}
                        checked={CanAcceptOrders}
                        onChange={(e) => setCanAcceptOrders(e.target.checked)}
                    />
                    <OdxCheckbox
                        label="Is Contractor"
                        id={"Doop_User_Edit_Contractor"}
                        checked={IsContractor}
                        onChange={(e) => setIsContractor(e.target.checked)}
                    />
                    <OdxCheckbox
                        label="Is Ext. Service Manager"
                        id={"Doop_User_Edit_ExternalSrvMng"}
                        checked={IsOrderInformed}
                        onChange={(e) => setIsOrderInformed(e.target.checked)}
                    />
                </div>
            </div>
        </OdxModal>
    );
};

export default EditUserModal;
